export interface VirtualScrollOptions<T> {
  container: HTMLElement;
  items: T[];
  itemHeight: number;
  bufferCount: number;
  renderItem: (item: T, index: number) => HTMLElement;
  getKey: (item: T) => string;
  layout: 'grid' | 'list';
  columns?: number;
}

export interface VirtualScroller {
  destroy: () => void;
  update: (items: unknown[]) => void;
}

export function createVirtualScroller<T>(opts: VirtualScrollOptions<T>): VirtualScroller {
  const { container, itemHeight, bufferCount, renderItem, getKey, layout } = opts;
  let items = opts.items;

  container.style.position = 'relative';
  container.innerHTML = '';

  const spacer = document.createElement('div');
  spacer.setAttribute('aria-hidden', 'true');
  container.appendChild(spacer);

  const content = document.createElement('div');
  if (layout === 'grid') {
    content.style.display = 'grid';
    content.style.gridTemplateColumns = `repeat(auto-fill, minmax(min(380px, 100%), 1fr))`;
    content.style.gap = 'var(--space-6)';
  } else {
    content.style.display = 'flex';
    content.style.flexDirection = 'column';
    content.style.gap = 'var(--space-3)';
  }
  container.appendChild(content);

  const bottomSpacer = document.createElement('div');
  bottomSpacer.setAttribute('aria-hidden', 'true');
  container.appendChild(bottomSpacer);

  let renderedKeys = new Set<string>();
  let animId: number | null = null;

  function getColumnCount(): number {
    const width = container.clientWidth || window.innerWidth;
    if (width < 768) return 1;
    if (width < 1100) return 2;
    return 3;
  }

  function render(): void {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const viewportHeight = window.innerHeight;
    const containerTop = container.getBoundingClientRect().top + scrollTop;

    const cols = layout === 'grid' ? getColumnCount() : 1;
    const rows = Math.ceil(items.length / cols);

    const relativeScroll = Math.max(0, scrollTop - containerTop);
    const startRow = Math.max(0, Math.floor(relativeScroll / itemHeight) - bufferCount);
    const visibleRows = Math.ceil(viewportHeight / itemHeight) + bufferCount * 2;
    const endRow = Math.min(rows, startRow + visibleRows);

    const startIndex = startRow * cols;
    const endIndex = Math.min(items.length, endRow * cols);

    spacer.style.height = `${startRow * itemHeight}px`;
    bottomSpacer.style.height = `${Math.max(0, (rows - endRow) * itemHeight)}px`;

    const visibleItems = items.slice(startIndex, endIndex);
    const newKeys = new Set(visibleItems.map(getKey));

    if (setsEqual(newKeys, renderedKeys)) return;
    renderedKeys = newKeys;

    content.innerHTML = '';
    visibleItems.forEach((item, i) => {
      const el = renderItem(item, startIndex + i);
      content.appendChild(el);
    });
  }

  function setsEqual(a: Set<string>, b: Set<string>): boolean {
    if (a.size !== b.size) return false;
    for (const v of a) if (!b.has(v)) return false;
    return true;
  }

  function onScroll(): void {
    if (animId !== null) return;
    animId = requestAnimationFrame(() => {
      animId = null;
      render();
    });
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  render();

  return {
    destroy() {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (animId !== null) cancelAnimationFrame(animId);
    },
    update(newItems: unknown[]) {
      items = newItems as T[];
      renderedKeys = new Set();
      render();
    },
  };
}

export const VIRTUAL_SCROLL_THRESHOLD = 20;
